/*
 * Copyright 2026 Sony Corporation
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  type ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';

import type { AppConfig } from '../config';

/**
 * Sign-in is SRP against the user pool directly: the pool is created with the
 * hosted UI and its OAuth flows disabled, so there is no redirect to follow and
 * no token ever reaches the URL bar.
 */
export interface SignedIn {
  kind: 'signed_in';
  session: CognitoUserSession;
}

/** Accounts are created by an administrator, so the first sign-in always lands here. */
export interface NewPasswordRequired {
  kind: 'new_password_required';
  complete: (newPassword: string) => Promise<SignedIn>;
}

export interface MfaRequired {
  kind: 'mfa_required';
  method: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA';
  complete: (code: string) => Promise<SignedIn>;
}

export type SignInResult = SignedIn | NewPasswordRequired | MfaRequired;

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export interface AuthService {
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => void;
  currentUserEmail: () => string | undefined;
  /** Resolves undefined when there is no valid session; refreshes when needed. */
  getIdToken: () => Promise<string | undefined>;
}

const poolData = (config: AppConfig): ICognitoUserPoolData => ({
  UserPoolId: config.user_pool_id,
  ClientId: config.user_pool_client_id,
});

export const createAuthService = (config: AppConfig): AuthService => {
  const pool = new CognitoUserPool(poolData(config));

  const signIn = (email: string, password: string): Promise<SignInResult> =>
    new Promise<SignInResult>((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: pool });

      const respondToChallenge = (
        respond: (callbacks: {
          onSuccess: (session: CognitoUserSession) => void;
          onFailure: (error: Error) => void;
        }) => void
      ) =>
        new Promise<SignedIn>((resolveChallenge, rejectChallenge) => {
          respond({
            onSuccess: (session) => resolveChallenge({ kind: 'signed_in', session }),
            onFailure: (error) => rejectChallenge(new AuthError(error.message)),
          });
        });

      user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
        onSuccess: (session) => resolve({ kind: 'signed_in', session }),
        onFailure: (error: Error) => reject(new AuthError(error.message)),
        newPasswordRequired: (userAttributes: Record<string, unknown>) => {
          // Cognito rejects these read-only attributes if they are echoed back.
          delete userAttributes.email_verified;
          delete userAttributes.email;
          resolve({
            kind: 'new_password_required',
            complete: (newPassword) =>
              respondToChallenge((callbacks) =>
                user.completeNewPasswordChallenge(newPassword, userAttributes, callbacks)
              ),
          });
        },
        totpRequired: (method: string) => {
          resolve({
            kind: 'mfa_required',
            method: method === 'SMS_MFA' ? 'SMS_MFA' : 'SOFTWARE_TOKEN_MFA',
            complete: (code) => respondToChallenge((callbacks) => user.sendMFACode(code, callbacks, method)),
          });
        },
        mfaRequired: (method: string) => {
          resolve({
            kind: 'mfa_required',
            method: method === 'SOFTWARE_TOKEN_MFA' ? 'SOFTWARE_TOKEN_MFA' : 'SMS_MFA',
            complete: (code) => respondToChallenge((callbacks) => user.sendMFACode(code, callbacks, method)),
          });
        },
      });
    });

  const getIdToken = (): Promise<string | undefined> =>
    new Promise((resolve) => {
      const user = pool.getCurrentUser();
      if (!user) {
        resolve(undefined);
        return;
      }
      // getSession refreshes an expired id token from the refresh token, so
      // callers never have to think about the one-hour lifetime.
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        resolve(error || !session?.isValid() ? undefined : session.getIdToken().getJwtToken());
      });
    });

  /**
   * The signed-in user's email, from the id token's own claim.
   *
   * Not `getUsername()`: this pool only aliases email for sign-in, so the
   * username is the generated uuid and showing it tells nobody who they are.
   * `getSession` answers from storage without a round trip when the token is
   * still valid, which is why this can stay synchronous.
   */
  const currentUserEmail = (): string | undefined => {
    const user = pool.getCurrentUser();
    if (!user) {
      return undefined;
    }

    let claim: string | undefined;
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      const value = error ? undefined : session?.getIdToken().payload.email;
      claim = typeof value === 'string' ? value : undefined;
    });
    return claim ?? user.getUsername();
  };

  return {
    signIn,
    getIdToken,
    signOut: () => pool.getCurrentUser()?.signOut(),
    currentUserEmail,
  };
};
